/* eslint-disable react/no-string-refs */
import Joi from '@hapi/joi';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { ITranslateProps, translate } from '../../mixins/translate';
import { ILanguageCodeProps, IValidationComponentState, ValidatingComponent } from '../../mixins/validation/validation';
import { fetchJson, fetchPost } from '../../store/fetch';
import { Roles } from '../../utils/enums/Roles';
import { IRouterProps } from '../../utils/IRouterProps';
import { isCustomerVersion } from '../../utils/isCustomerVersion';
import {
    Content,
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelExplanation,
    EditPanelHeader,
    EditPanelInput,
    EditPanelRow,
    EditPanelSection,
    EditPanelSelect,
    EditPanelSwitch,
    EditPanelTextArea,
    Panel,
    PanelBody,
    Topbar,
    TopbarCrumb,
    TopbarCrumbActive,
    TopbarLeft,
    Tray,
} from '../elements';
import { DisplayMessage as DisplayError } from '../utils/displayError';

interface IUserAddEditProps extends ITranslateProps, ILanguageCodeProps, IRouterProps {
    dispatch: (prop: any) => void;
    params: any;
}

interface IUserAddEditState extends IValidationComponentState {
    message?: undefined | string;
    submitting: boolean;
    failed: boolean;
    errorCode?: undefined | string;
    existingUserId?: undefined | string;
    firstName?: undefined | string;
    lastName?: undefined | string;
    emailAddress?: undefined | string;
    mobileNumber?: undefined | string;
    officeNumber?: undefined | string;
    status?: undefined | string;
    language?: undefined | string;
    companyId?: undefined | string;
    companyName?: undefined | string;
    companyRole?: undefined | string;
    companyIsClient?: undefined | boolean;
    password?: undefined | string;
    repeatPassword?: undefined | string;
    isReadOnly: boolean;
    isAccountManager: boolean;
    isConfigurationManager: boolean;
    isDroneOperator: boolean;
    generatePasswordLink: boolean;
    hasOtpEnabled: boolean;
    emailMessage?: undefined | string;
}

function mapStateToProps(state: any) {
    const { language } = state;
    return {
        languageCode: language.code,
    };
}

class UserAddEdit extends ValidatingComponent<IUserAddEditProps, IUserAddEditState> {
    constructor(props: IUserAddEditProps) {
        super(props);
        this.extraValidation = this.extraValidation.bind(this);

        this.state = {
            submitting: false,
            failed: false,
            companyId: this.props.location.query.companyId,
            language: 'nl',
            status: 'enabled',
            isReadOnly: false,
            isAccountManager: false,
            isConfigurationManager: false,
            isDroneOperator: false,
            companyIsClient: true,
            generatePasswordLink: true,
            errors: {},
            hasOtpEnabled: false,
        };
    }

    public override async componentDidMount() {
        if (this.props.match.params.id) {
            await this.loadUser();
        } else {
            await this.loadCompany();
        }
    }

    public getValidatorData() {
        return {
            firstName: this.state.firstName,
            lastName: this.state.lastName,
            emailAddress: this.state.emailAddress,
            mobileNumber: this.state.mobileNumber,
            officeNumber: this.state.officeNumber,
            status: this.state.status,
            language: this.state.language,
            password: this.state.password,
            isReadOnly: this.state.isReadOnly,
            isAccountManager: this.state.isAccountManager,
            isConfigurationManager: this.state.isConfigurationManager,
            isDroneOperator: this.state.isDroneOperator,
            generatePasswordLink: this.state.generatePasswordLink,
            emailMessage: this.state.emailMessage,
        };
    }

    public override extraValidation(): any {
        const result = {} as { repeatPassword?: string[] };
        if (this.props.match.params.id || !this.state.generatePasswordLink) {
            if (this.state.password !== this.state.repeatPassword) {
                result.repeatPassword = [this.props.t('profile.errorPasswordsNotEqual')];
            }
        }
        return result;
    }

    public getValidatorTypes(): any {
        return {
            firstName: Joi.string().required().label(this.props.t('user.firstName')),
            lastName: Joi.string().required().label(this.props.t('user.lastName')),
            emailAddress: Joi.string().required().label(this.props.t('user.emailAddress')),
        };
    }

    public override componentDidCatch(error: any) {
        void this.setState({ submitting: false, failed: true, errorCode: error, existingUserId: undefined });
    }

    public override render() {
        return (
            <>
                <Topbar>
                    <TopbarLeft>
                        {(this.props.match.params.id && (
                            <TopbarCrumbActive title={this.props.t('common.editItem', this.props.t('common.user'))} icon="fa-user" />
                        )) || <TopbarCrumbActive title={this.props.t('common.addItem', this.props.t('common.user'))} icon="fa-user" />}
                        <TopbarCrumb>
                            <Link to={`/companies/${this.state.companyId}`}>{this.state.companyName}</Link>
                        </TopbarCrumb>
                        <TopbarCrumb>
                            <Link to="/companies">{this.props.t('common.companies')}</Link>
                        </TopbarCrumb>
                    </TopbarLeft>
                </Topbar>

                <Content>
                    <Tray>
                        {this.renderMessage()}
                        <Panel small>
                            <form method="post" action="/" id="admin-form">
                                <PanelBody>
                                    {/* First last name*/}
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <EditPanelInput
                                                name="firstName"
                                                placeholder={this.props.t('user.firstName')}
                                                value={this.state.firstName}
                                                icon="fa fa-user"
                                                notValid={!this.isValid('firstName')}
                                                handleChange={this.handleChange.bind(this)}
                                                handleBlur={this.handleValidation.bind(this)}
                                            />
                                            {this.renderHelpText(this.getValidationMessages('firstName'))}
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <EditPanelInput
                                                name="lastName"
                                                placeholder={this.props.t('user.lastName')}
                                                value={this.state.lastName}
                                                icon="fa fa-user"
                                                notValid={!this.isValid('lastName')}
                                                handleChange={this.handleChange.bind(this)}
                                                handleBlur={this.handleValidation.bind(this)}
                                            />
                                            {this.renderHelpText(this.getValidationMessages('lastName'))}
                                        </EditPanelCell>
                                    </EditPanelRow>

                                    {/* User e-mail*/}
                                    <EditPanelSection>
                                        <EditPanelInput
                                            name="emailAddress"
                                            value={this.state.emailAddress}
                                            placeholder={this.props.t('user.emailAddress')}
                                            notValid={!this.isValid('emailAddress')}
                                            handleChange={this.handleChange.bind(this)}
                                            handleBlur={this.handleValidation.bind(this)}
                                            icon="fa fa-user"
                                        />
                                        {this.renderHelpText(this.getValidationMessages('emailAddress'))}
                                    </EditPanelSection>

                                    {/* Phone number */}
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <EditPanelInput
                                                name="mobileNumber"
                                                placeholder={this.props.t('user.mobileNumber')}
                                                value={this.state.mobileNumber}
                                                icon="fa fa-user"
                                                handleChange={this.handleChange.bind(this)}
                                                handleBlur={this.handleValidation.bind(this)}
                                            />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <EditPanelInput
                                                name="officeNumber"
                                                placeholder={this.props.t('user.officeNumber')}
                                                value={this.state.officeNumber}
                                                icon="fa fa-user"
                                                handleChange={this.handleChange.bind(this)}
                                                handleBlur={this.handleValidation.bind(this)}
                                            />
                                        </EditPanelCell>
                                    </EditPanelRow>

                                    {/* Password */}
                                    <EditPanelHeader text={this.props.t('user.password')} />
                                    {(!this.props.match.params.id && (
                                        <EditPanelSection>
                                            <EditPanelSwitch
                                                name="generatePasswordLink"
                                                placeholder={this.props.t('user.generatePasswordLink')}
                                                value={this.state.generatePasswordLink}
                                                handleChange={this.handleChange.bind(this)}
                                            />
                                            <EditPanelExplanation text={this.props.t('user.generatePasswordLinkExplanation')} />
                                        </EditPanelSection>
                                    )) || (
                                        <EditPanelSection>
                                            <EditPanelButton
                                                disabled={this.state.submitting}
                                                text={this.props.t('user.resetAccount')}
                                                small={true}
                                                noLeftMargin={true}
                                                onClick={async () => this.resetAcccount()}
                                            />
                                            <EditPanelExplanation text={this.props.t('user.resetAccountExplanation')} />
                                        </EditPanelSection>
                                    )}
                                    {((this.props.match.params.id || !this.state.generatePasswordLink) && (
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={6}>
                                                <label
                                                    htmlFor="password"
                                                    className={this.getClassesWithDefault('password', { field: true, 'prepend-icon': true })}
                                                >
                                                    <input
                                                        type="password"
                                                        name="password"
                                                        id="password"
                                                        className="gui-input"
                                                        placeholder={this.props.t('user.password')}
                                                        onBlur={() => this.handleValidation('password')}
                                                        onChange={async (e) => this.handleChange(e.target.value, 'password')}
                                                    />
                                                    <label htmlFor="password" className="field-icon">
                                                        <i className="fa fa-lock"></i>
                                                    </label>
                                                </label>
                                                {this.renderHelpText(this.getValidationMessages('password'))}
                                            </EditPanelCell>
                                            <EditPanelCell rowCount={6}>
                                                <label
                                                    htmlFor="repeatPassword"
                                                    className={this.getClassesWithDefault('repeatPassword', { field: true, 'prepend-icon': true })}
                                                >
                                                    <input
                                                        type="password"
                                                        name="repeatPassword"
                                                        id="repeatPassword"
                                                        className="gui-input"
                                                        placeholder={this.props.t('user.repeatPassword')}
                                                        onChange={(e) => void this.handleChange(e.target.value, 'repeatPassword')}
                                                        onBlur={() => this.handleValidation('repeatPassword')}
                                                    />
                                                    <label htmlFor="repeatPassword" className="field-icon">
                                                        <i className="fa fa-lock"></i>
                                                    </label>
                                                </label>
                                                {this.renderHelpText(this.getValidationMessages('repeatPassword'))}
                                            </EditPanelCell>
                                        </EditPanelRow>
                                    )) || (
                                        <EditPanelSection>
                                            <EditPanelTextArea
                                                name="emailMessage"
                                                value={this.state.emailMessage}
                                                placeholder={this.props.t('user.emailMessage')}
                                                handleChange={this.handleChange.bind(this)}
                                            />
                                        </EditPanelSection>
                                    )}
                                    {this.props.match.params.id && <EditPanelExplanation text={this.props.t('user.passwordExplanation')} />}
                                    {/* 2FA */}
                                    {this.props.match.params.id && (
                                        <>
                                            <EditPanelHeader text={this.props.t('user.otp')} />
                                            {(this.state.hasOtpEnabled && (
                                                <>
                                                    <EditPanelSection>
                                                        <EditPanelButton
                                                            disabled={this.state.submitting}
                                                            text={this.props.t('user.resetOtp')}
                                                            small={true}
                                                            noLeftMargin={true}
                                                            onClick={async () => this.resetOtp()}
                                                        />
                                                        <EditPanelExplanation text={this.props.t('user.resetOtpExplanation')} />
                                                    </EditPanelSection>
                                                </>
                                            )) || <EditPanelSection>{this.props.t('common.disabled')}</EditPanelSection>}
                                        </>
                                    )}

                                    {/* Status Language */}
                                    <EditPanelHeader text={this.props.t('user.statusLanguage')} />
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <EditPanelSelect
                                                name="status"
                                                value={this.state.status}
                                                handleChange={this.handleChange.bind(this)}
                                                options={[
                                                    { value: 'enabled', text: this.props.t('common.enabled') },
                                                    { value: 'disabled', text: this.props.t('common.disabled') },
                                                ]}
                                            />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <EditPanelSelect
                                                name="language"
                                                value={this.state.language}
                                                handleChange={this.handleChange.bind(this)}
                                                options={[
                                                    { value: 'nl', text: this.props.t('user.dutch') },
                                                    { value: 'en', text: this.props.t('user.english') },
                                                ]}
                                            />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelSection>
                                        <EditPanelSwitch
                                            name="isReadOnly"
                                            placeholder={this.props.t('user.isReadOnly')}
                                            value={this.state.isReadOnly}
                                            handleChange={this.handleChange.bind(this)}
                                        />
                                        <EditPanelExplanation text={this.props.t('user.isReadOnlyExplanation')} />
                                    </EditPanelSection>
                                    {(this.state.companyRole === Roles.Administrator || this.state.companyIsClient) && (
                                        <>
                                            <EditPanelHeader text={this.props.t('user.managerRoles')} />
                                            <EditPanelRow>
                                                <EditPanelCell rowCount={6}>
                                                    <EditPanelSwitch
                                                        name="isAccountManager"
                                                        placeholder={this.props.t('user.isAccountManager')}
                                                        value={this.state.isAccountManager}
                                                        handleChange={this.handleChange.bind(this)}
                                                    />
                                                </EditPanelCell>
                                                <EditPanelCell rowCount={6}>
                                                    <EditPanelSwitch
                                                        name="isConfigurationManager"
                                                        placeholder={this.props.t('user.isConfigurationManager')}
                                                        value={this.state.isConfigurationManager}
                                                        handleChange={this.handleChange.bind(this)}
                                                    />
                                                </EditPanelCell>
                                            </EditPanelRow>

                                            {this.state.companyIsClient && isCustomerVersion('ronik') && (
                                                <EditPanelRow>
                                                    <EditPanelCell rowCount={6}>
                                                        <EditPanelSwitch
                                                            name="isDroneOperator"
                                                            placeholder={this.props.t('user.isDroneOperator')}
                                                            value={this.state.isDroneOperator}
                                                            handleChange={this.handleChange.bind(this)}
                                                        />
                                                    </EditPanelCell>
                                                </EditPanelRow>
                                            )}
                                        </>
                                    )}
                                </PanelBody>
                                <EditPanelButtons footer={true}>
                                    <EditPanelButton
                                        onClick={() => this.props.navigate(`/companies/${this.state.companyId}`)}
                                        text={this.props.t('common.cancel')}
                                        small={false}
                                        disabled={this.state.submitting}
                                    />
                                    <EditPanelButton
                                        onClick={() => this.saveClicked()}
                                        text={this.props.t('common.save')}
                                        small={false}
                                        primary={true}
                                        disabled={this.state.submitting}
                                    />
                                </EditPanelButtons>
                            </form>
                        </Panel>
                    </Tray>
                </Content>
            </>
        );
    }

    protected override renderMessage() {
        if (this.state.failed && this.state.errorCode === 'EMAIL_ALREADY_IN_COMPANY') {
            return <DisplayError small message={this.props.t('user.errorEmailAlreadyInCompany')} />;
        }

        if (this.state.failed && this.state.errorCode === 'EMAIL_ALREADY_IN_TENANT') {
            return <DisplayError small message={this.props.t('user.errorEmailAlreadyInTenant')} />;
        }

        if (this.state.failed && this.state.errorCode === 'EMAIL_NOT_UNIQUE') {
            return <DisplayError small message={this.props.t('user.errorEmailNotUnique')} />;
        }

        return super.renderMessage();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected override async onChanged(_value: any, _stateKey: string) {
        if (this.state.isReadOnly) {
            await this.setStatePromised({ isConfigurationManager: false, isAccountManager: false });
        }
    }

    private async loadUser() {
        const url = `/api/users/${this.props.match.params.id}`;
        await this.setStatePromised({ submitting: true });
        const result = await fetchJson(url, 'GET');
        if (result.successful) {
            await this.setStatePromised(result.json);
            await this.setStatePromised({ failed: false, submitting: false });
        } else {
            await this.setStatePromised({ submitting: false, failed: true, errorCode: result.errorCode, existingUserId: undefined });
        }
    }

    private async loadCompany() {
        const url = `/api/companies/${this.props.location.query.companyId}`;
        await this.setStatePromised({ submitting: true });
        const result = await fetchJson(url, 'GET');
        if (result.successful) {
            await this.setStatePromised({
                companyName: result.json.name,
                companyRole: result.json.role,
                companyIsClient: result.json.isClient,
                failed: false,
                submitting: false,
            });
        } else {
            await this.setStatePromised({ submitting: false, failed: true, errorCode: result.errorCode, existingUserId: undefined });
        }
    }

    private saveClicked() {
        this.validate(async (validationError: any) => {
            if (!validationError) {
                await this.setStatePromised({ message: undefined, submitting: true, errorCode: undefined, existingUserId: undefined, failed: false });
                const data = this.getValidatorData() as any;

                let url = '/api/users';
                // if we have an that.props.params.id it is an update.
                if (this.props.match.params.id) {
                    url += `/${this.props.match.params.id}`; // userID in URL for update
                } else {
                    url += `?companyId=${this.state.companyId}`; // companyID in URL for create
                }
                try {
                    const result = await fetchPost(url, {}, data, [
                        'EMAIL_NOT_UNIQUE',
                        'EMAIL_ALREADY_IN_COMPANY',
                        'EMAIL_ALREADY_IN_TENANT',
                        'WRONG_PASSWORD',
                        'PASSWORD_TOO_SHORT',
                        'PASSWORD_NO_PUNCTUATION',
                        'NOT_ALLOWED',
                        'EMAIL_NOT_VALID',
                    ]);

                    if (result.errorCode && result.errorCode !== '') {
                        await this.setStatePromised({
                            submitting: false,
                            errorCode: result.errorCode,
                            existingUserId: result.data?.userId,
                            failed: true,
                        });
                    } else {
                        await this.setStatePromised({ submitting: false, failed: false });
                        this.props.navigate(`/companies/${this.state.companyId}`);
                    }
                } catch (error) {
                    let errorCode: string | undefined = undefined;
                    if ('errorCode' in error) {
                        // eslint-disable-next-line prefer-destructuring
                        errorCode = error.errorCode;
                    } else if ('statusCode' in error) {
                        errorCode = error.statusCode;
                    }
                    await this.setStatePromised({ submitting: false, errorCode, failed: true });
                }
            }
        });
    }

    private async resetAcccount() {
        await this.setStatePromised({ message: undefined, submitting: true, errorCode: undefined, existingUserId: undefined, failed: false });
        const result = await fetchPost(`/api/users/${this.props.match.params.id}/reset`, {}, {}, ['NOT_ALLOWED']);
        if (result.errorCode && result.errorCode !== '') {
            await this.setStatePromised({ submitting: false, errorCode: result.errorCode, failed: true });
        } else {
            await this.setStatePromised({ submitting: false, failed: false });
            this.props.navigate(`/companies/${this.state.companyId}`);
        }
    }

    private async resetOtp() {
        await this.setStatePromised({ message: undefined, submitting: true, errorCode: undefined, existingUserId: undefined, failed: false });
        const result = await fetchPost(`/api/users/${this.props.match.params.id}/otpreset`, {}, {}, ['NOT_ALLOWED']);
        if (result.errorCode && result.errorCode !== '') {
            await this.setStatePromised({ submitting: false, errorCode: result.errorCode, existingUserId: undefined, failed: true });
        } else {
            await this.setStatePromised({ submitting: false, failed: false });
            this.props.navigate(`/companies/${this.state.companyId}`);
        }
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(connect(mapStateToProps, undefined, undefined, { forwardRef: true })(UserAddEdit as any) as any);
